import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useJwt, useAuthHeaders, furnitureCatalog, furnitureFootprint, FURNITURE_CATEGORIES, MAX_DESK_SIZE, MAX_DESK_ITEMS,
} from '@campus/core';
import type { FurnitureCatalogEntry } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { Button, Icon, Input, Modal, Tabs, cx } from '../ds/index.js';
import { GridImage } from './FurniturePalette.js';

/** GET /admin/desks — voir PersonalDeskService.adminList (apps/api). */
interface DeskRow {
  id: string;
  displayName: string;
  mapId: string;
  areaName: string;
  itemCount: number;
  createdAt: string;
  /** Sur un emplacement (false = posé librement : OWNER/ADMIN, ou d'avant les emplacements). */
  onSlot: boolean;
}

type Tab = 'desks' | 'catalog';

/**
 * AdminDesks — Admin › Bureaux : les bureaux posés par les membres
 * (vider, supprimer) et les objets autorisés pour les aménager.
 */
export function AdminDesks() {
  const [tab, setTab] = useState<Tab>('desks');
  return (
    <>
      <header className="pl-page-head">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 className="pl-page-title">Bureaux</h1>
          <p className="pl-page-sub">
            Chaque membre pose son bureau dans un « Espace bureaux » (éditeur › Organisation › Zones) et l&apos;aménage avec les objets autorisés ici.
          </p>
        </div>
      </header>
      <div><Tabs aria-label="Bureaux ou catalogue" items={[{ id: 'desks', label: 'Bureaux' }, { id: 'catalog', label: 'Objets autorisés' }]} value={tab} onChange={setTab} /></div>
      {tab === 'desks' ? <DesksTab /> : <CatalogTab />}
    </>
  );
}

// ── Bureaux ──────────────────────────────────────────────────

function DesksTab() {
  const jwt = useJwt();
  const headers = useAuthHeaders(getTenantSlug());
  const [desks, setDesks] = useState<DeskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ desk: DeskRow; itemsOnly: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!jwt) return;
    setLoading(true);
    fetch(`${API_BASE}/admin/desks`, { headers })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: DeskRow[]) => setDesks(data))
      .catch(() => setError('Impossible de charger les bureaux.'))
      .finally(() => setLoading(false));
  }, [jwt]);

  useEffect(() => { load(); }, [load]);

  async function remove() {
    if (!confirm) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/admin/desks/${encodeURIComponent(confirm.desk.id)}${confirm.itemsOnly ? '?itemsOnly=1' : ''}`, { method: 'DELETE', headers });
      if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
      setConfirm(null);
      load();
    } catch {
      setError(confirm.itemsOnly ? 'Le bureau n’a pas pu être vidé.' : 'Le bureau n’a pas pu être supprimé.');
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  const n = desks.length;
  return (
    <>
      {error && (
        <div className="pl-alert" role="alert" style={{ justifyContent: 'space-between' }}>
          <span>{error}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setError(null)} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <p className="pl-page-sub" style={{ margin: 0 }}>{loading ? 'Chargement…' : `${n} bureau${n > 1 ? 'x' : ''} posé${n > 1 ? 's' : ''}`}</p>
        <Button variant="secondary" icon="refresh" onClick={load} disabled={loading}>Actualiser</Button>
      </div>

      {!loading && (
        <div className="pl-panel" style={{ overflow: 'hidden' }}>
          <table className="pl-table">
            <thead><tr><th>Membre</th><th>Carte</th><th>Espace</th><th>Place</th><th>Objets</th><th>Posé le</th><th><span className="pl-sr">Actions</span></th></tr></thead>
            <tbody>
              {n === 0 && (
                <tr><td colSpan={7} className="pl-muted" style={{ textAlign: 'center', padding: 24 }}>
                  Aucun bureau pour l&apos;instant. Les membres prennent le leur en jeu, sur un emplacement libre d&apos;un « Espace bureaux ».
                </td></tr>
              )}
              {desks.map((d) => (
                <tr key={d.id}>
                  <td style={{ fontWeight: 500 }}>{d.displayName}</td>
                  <td className="pl-muted">{d.mapId}</td>
                  <td>{d.areaName}</td>
                  <td className="pl-muted">{d.onSlot ? 'Emplacement' : 'Pose libre'}</td>
                  <td className="pl-num">{d.itemCount} / {MAX_DESK_ITEMS}</td>
                  <td className="pl-muted pl-num">{new Date(d.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {d.itemCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => setConfirm({ desk: d, itemsOnly: true })}>Vider</Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setConfirm({ desk: d, itemsOnly: false })}>Supprimer</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <Modal
          title={confirm.itemsOnly ? `Vider le bureau de ${confirm.desk.displayName} ?` : `Supprimer le bureau de ${confirm.desk.displayName} ?`}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Annuler</Button>
              <Button variant="danger" onClick={() => { void remove(); }} disabled={busy}>{confirm.itemsOnly ? 'Vider' : 'Supprimer'}</Button>
            </>
          }
        >
          <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            {confirm.itemsOnly
              ? `Les ${confirm.desk.itemCount} objets posés sont retirés. Le bureau reste en place.`
              : confirm.desk.onSlot
                ? 'Le bureau et tout son aménagement sont supprimés. L\u2019emplacement redevient libre : la personne pourra en prendre un nouveau.'
                : 'Le bureau et tout son aménagement sont supprimés. La personne pourra en prendre un nouveau.'}
          </p>
        </Modal>
      )}
    </>
  );
}

// ── Objets autorisés ─────────────────────────────────────────

const THUMB_CELL = 24;
const THUMB_MAX_WIDTH = 220;

/** Objets proposables : visibles (animés compris), qui tiennent dans le plus grand bureau possible. */
const CANDIDATES: FurnitureCatalogEntry[] = furnitureCatalog.filter((e) => {
  if (e.hidden) return false;
  const f = furnitureFootprint(e);
  return f.width <= MAX_DESK_SIZE.width && f.height <= MAX_DESK_SIZE.height;
});

function CatalogTab() {
  const jwt = useJwt();
  const headers = useAuthHeaders(getTenantSlug());
  const [saved, setSaved] = useState<{ ids: Set<string>; isDefault: boolean } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const apply = (data: { furnitureIds: string[]; isDefault: boolean }) => {
    const ids = new Set(data.furnitureIds);
    setSaved({ ids, isDefault: data.isDefault });
    setSelected(new Set(ids));
  };

  useEffect(() => {
    if (!jwt) return;
    fetch(`${API_BASE}/admin/desks/catalog`, { headers })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(apply)
      .catch(() => setMessage({ tone: 'danger', text: 'Impossible de charger les objets autorisés.' }));
  }, [jwt]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items = CANDIDATES.filter((e) => !q || e.label.toLowerCase().includes(q));
    return FURNITURE_CATEGORIES
      .map((c) => ({ id: c.id, label: c.label, items: items.filter((e) => e.category === c.id) }))
      .filter((g) => g.items.length > 0);
  }, [search]);

  const dirty = !!saved && (saved.ids.size !== selected.size || [...selected].some((id) => !saved.ids.has(id)));

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setAll = (ids: string[], on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    for (const id of ids) { if (on) next.add(id); else next.delete(id); }
    return next;
  });

  async function save(furnitureIds: string[] | null) {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/admin/desks/catalog`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ furnitureIds }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      apply(await res.json());
      setMessage({ tone: 'success', text: furnitureIds === null ? 'Sélection par défaut rétablie.' : 'Objets autorisés enregistrés.' });
    } catch {
      setMessage({ tone: 'danger', text: 'Les objets autorisés n’ont pas pu être enregistrés.' });
    } finally {
      setBusy(false);
    }
  }

  if (!saved) return message ? <div className="pl-alert" role="alert">{message.text}</div> : <p className="pl-page-sub">Chargement…</p>;

  return (
    <>
      {message && (
        <div className={cx('pl-alert', message.tone === 'success' && 'pl-alert-success')} role={message.tone === 'success' ? 'status' : 'alert'} style={{ justifyContent: 'space-between' }}>
          <span>{message.text}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setMessage(null)} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <p className="pl-page-sub" style={{ margin: 0, flex: 1, minWidth: 240 }}>
          {selected.size} objet{selected.size > 1 ? 's' : ''} autorisé{selected.size > 1 ? 's' : ''}
          {saved.isDefault && !dirty ? ' · sélection par défaut' : ''}. Clique sur un objet pour l&apos;autoriser ou le retirer.
          Un objet retiré reste affiché là où il est déjà posé, mais devra être enlevé à la prochaine modification du bureau.
        </p>
        <Button variant="ghost" onClick={() => { void save(null); }} disabled={busy || (saved.isDefault && !dirty)}>Rétablir la sélection par défaut</Button>
        <Button variant="ghost" onClick={() => setSelected(new Set(saved.ids))} disabled={busy || !dirty}>Annuler</Button>
        <Button variant="primary" onClick={() => { void save([...selected]); }} disabled={busy || !dirty}>Enregistrer</Button>
      </div>

      <div style={{ maxWidth: 360 }}>
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un objet" aria-label="Rechercher un objet" />
      </div>

      <div className="pl-panel" style={{ padding: '4px 8px 8px' }}>
        {groups.map((g) => {
          const isOpen = search.trim() !== '' || open.has(g.id);
          const ids = g.items.map((e) => e.id);
          const count = ids.filter((id) => selected.has(id)).length;
          return (
            <div key={g.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  className="pl-cat"
                  aria-expanded={isOpen}
                  style={{ flex: 1 }}
                  onClick={() => setOpen((prev) => { const next = new Set(prev); if (next.has(g.id)) next.delete(g.id); else next.add(g.id); return next; })}
                >
                  <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={14} />
                  {g.label}
                  <span className="pl-count">{count} / {ids.length}</span>
                </button>
                <Button variant="ghost" size="sm" onClick={() => setAll(ids, true)} disabled={count === ids.length}>Tout autoriser</Button>
                <Button variant="ghost" size="sm" onClick={() => setAll(ids, false)} disabled={count === 0}>Tout retirer</Button>
              </div>
              {isOpen && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 6, padding: '4px 12px 12px' }}>
                  {g.items.map((entry) => {
                    const on = selected.has(entry.id);
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        aria-pressed={on}
                        aria-label={entry.label}
                        title={`${entry.label}${on ? ' · autorisé' : ''}`}
                        className={cx('pl-fthumb', on && 'pl-fthumb-on')}
                        style={on ? undefined : { opacity: 0.4 }}
                        onClick={() => toggle(entry.id)}
                      >
                        <GridImage entry={entry} cell={THUMB_CELL} maxWidth={THUMB_MAX_WIDTH} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
